import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../api';
import AuthContext from '../contexts/AuthContext';
import UploadModal from '../components/UploadModal';
import { formatTime } from '../utils/timeUtils';

const containerStyle = {
  padding: '1.5rem',
  maxWidth: '1200px',
  margin: '0 auto'
};

const gridStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.75rem',
  marginTop: '1.5rem',
  backgroundColor: '#f8f9fa',
  padding: '0.75rem',
  borderRadius: '8px',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
};

const baseTileStyle = {
  backgroundColor: '#ffffff',
  borderRadius: '8px',
  padding: '1rem',
  border: '1px solid #e9ecef',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  boxShadow: '0 1px 4px rgba(0, 0, 0, 0.08)',
  position: 'relative',
  width: '100%',
  marginBottom: '0.75rem',
  textAlign: 'left'
};


const tileHeaderStyle = {
  marginBottom: '1rem',
  width: '100%'
};

const tileTitleStyle = {
  fontSize: '1.1rem',
  fontWeight: '600',
  color: '#2c3e50',
  margin: 0,
  flex: '1',
  minWidth: '150px',
  textDecoration: 'none'
};

const tileCategoryStyle = {
  background: 'linear-gradient(45deg, #6c757d, #495057)',
  color: 'white',
  padding: '0.2rem 0.5rem',
  borderRadius: '16px',
  fontSize: '0.75rem',
  fontWeight: '500',
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
  margin: '0.5rem'
};

const badgeStyle = {
  backgroundColor: '#28a745',
  color: 'white',
  padding: '0.25rem 0.75rem',
  borderRadius: '20px',
  fontSize: '0.875rem',
  fontWeight: '500',
  textTransform: 'uppercase',
  marginLeft: '0.5rem'
};

const premiumBadgeStyle = {
  ...badgeStyle,
  background: 'linear-gradient(45deg, #8e44ad, #9b59b6)'
};

const liveBadgeStyle = {
  ...badgeStyle,
  background: 'linear-gradient(45deg, #e74c3c, #c0392b)'
};

const tileStatsStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(20px, 1fr))',
  gap: '0.4rem',
  marginTop: '0.75rem',
  width: '100%'
};

const statItemStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '0.1rem',
  background: 'rgba(178, 185, 192, 0.08)',
  borderRadius: '4px',
  borderLeft: '2px solid #c8ced3ff',
  transition: 'all 0.2s ease'
};


const statLabelStyle = {
  fontSize: '0.7rem',
  color: '#6c757d',
  margin: 0,
  fontWeight: '500',
  textAlign: 'center'
};

const statValueStyle = {
  fontSize: '0.9rem',
  fontWeight: '600',
  color: '#2c3e50',
  margin: '0.1rem 0 0 0',
  textAlign: 'center'
};

const lockIconStyle = {
  opacity: 0.7,
  fontSize: '1rem',
  marginLeft: '0.5rem',
  color: '#e74c3c'
};

const loadingStyle = {
  textAlign: 'center',
  padding: '3rem'
};

const spinnerStyle = {
  border: '4px solid #f3f3f3',
  borderTop: '4px solid #3498db',
  borderRadius: '50%',
  width: '50px',
  height: '50px',
  animation: 'spin 1s linear infinite',
  margin: '0 auto 1rem auto'
};

const skeletonTileStyle = {
  height: '120px',
  background: 'linear-gradient(90deg, #e9ecef 25%, #dee2e6 50%, #e9ecef 75%)',
  backgroundSize: '200% 100%',
  animation: 'shimmer 2s infinite',
  borderRadius: '8px',
  marginBottom: '1rem'
};

const paginationStyle = {
  display: 'flex',
  justifyContent: 'center',
  padding: '2rem',
  gap: '0.5rem'
};

const buttonStyle = {
  padding: '0.75rem 1.5rem',
  borderRadius: '8px',
  border: '1px solid #ddd',
  backgroundColor: '#f8f9fa',
  cursor: 'pointer',
  fontWeight: '500',
  transition: 'all 0.2s ease'
};

const activeButtonStyle = {
  ...buttonStyle,
  backgroundColor: '#3498db',
  color: 'white',
  borderColor: '#3498db'
};

const disabledButtonStyle = {
  ...buttonStyle,
  opacity: '0.6',
  cursor: 'not-allowed'
};

// 🌈 Common Base Style
const baseButtonStyle = {
  border: 'none',
  borderRadius: '12px',
  color: '#fff',
  fontWeight: 600,
  cursor: 'pointer',
  padding: '0.6rem 1.3rem',
  transition: 'all 0.25s ease',
  boxShadow: '0 4px 10px rgba(0, 0, 0, 0.15)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '0.4rem',
  height: '46px',
  letterSpacing: '0.3px',
  backdropFilter: 'blur(8px)',
};

// ✨ Hover Lift Effect (universal)
const hoverLift = {
  transform: 'translateY(-3px)',
  boxShadow: '0 10px 20px rgba(0,0,0,0.25)',
  filter: 'brightness(1.05)',
};

// 📊 Results Button – Blue Gradient
const resultsButtonStyle = {
  ...baseButtonStyle,
  background: 'linear-gradient(135deg, #3b82f6, #06b6d4)',
  boxShadow: '0 4px 14px rgba(59, 130, 246, 0.4)',
};

// 🎧 Dictation Button – Purple Glow
const dictationButtonStyle = {
  ...baseButtonStyle,
  background: 'linear-gradient(135deg, #a855f7, #ec4899)',
  boxShadow: '0 4px 14px rgba(168, 85, 247, 0.4)',
};

// ▶ Play Button – Green Energy
const playButtonStyle = {
  ...baseButtonStyle,
  background: 'linear-gradient(135deg, #22c55e, #16a34a)',
  boxShadow: '0 4px 14px rgba(34, 197, 94, 0.4)',
  fontSize: '0.95rem',
  padding: '0.65rem 1.5rem',
};

// ⚡ Upgrade Button – Orange Gold Gradient
const upgradeButtonStyle = {
  ...baseButtonStyle,
  background: 'linear-gradient(135deg, #f59e0b, #f97316)',
  boxShadow: '0 4px 14px rgba(249, 115, 22, 0.4)',
};

// 🗑️ Delete Button – Red Fire
const deleteButtonStyle = {
  ...baseButtonStyle,
  background: 'linear-gradient(135deg, #ef4444, #b91c1c)',
  boxShadow: '0 4px 14px rgba(239, 68, 68, 0.4)',
  fontWeight: 500,
  fontSize: '14px',
  padding: '0.5rem 1rem',
  borderRadius: '8px',
};

// ✏️ Edit Button – Sky Blue Shine
const editButtonStyle = {
  ...baseButtonStyle,
  background: 'linear-gradient(135deg, #0ea5e9, #2563eb)',
  boxShadow: '0 4px 14px rgba(14, 165, 233, 0.4)',
};

const style = document.createElement('style');
style.innerHTML = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  @keyframes shimmer {
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
  }
  .tile-hover:hover {
    transform: translateY(-2px) !important;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
    border-color: #3498db !important;
    background-color: #ffffff !important;
  }
  .stat-hover:hover {
    background: rgba(52, 152, 219, 0.1) !important;
    border-left: 3px solid #3498db !important;
    transform: translateY(-1px) !important;
  }
  .results-btn:hover {
    ${hoverLift.transform};
    ${hoverLift.boxShadow};
    ${hoverLift.filter};
  }
  .dictation-btn:hover {
    ${hoverLift.transform};
    ${hoverLift.boxShadow};
    ${hoverLift.filter};
  }
  .play-btn:hover {
    ${hoverLift.transform};
    ${hoverLift.boxShadow};
    ${hoverLift.filter};
  }
  .upgrade-btn:hover {
    ${hoverLift.transform};
    ${hoverLift.boxShadow};
    ${hoverLift.filter};
  }
  .delete-btn:hover {
    ${hoverLift.transform};
    ${hoverLift.boxShadow};
    ${hoverLift.filter};
  }
  .edit-btn:hover {
    ${hoverLift.transform};
    ${hoverLift.boxShadow};
    ${hoverLift.filter};
  }
`;
document.head.appendChild(style);

const TestList = () => {
  const [tests, setTests] = useState([]);
  const { user } = useContext(AuthContext);
  const [showModal, setShowModal] = useState(false);
  const [editingTest, setEditingTest] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalTests, setTotalTests] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [testTypeFilter, setTestTypeFilter] = useState('all'); // 'all', 'free', 'premium'
  const [showExpectedModal, setShowExpectedModal] = useState(false);
  const [expectedText, setExpectedText] = useState('');
  const [selectedTestId, setSelectedTestId] = useState(null);
  const testsPerPage = 10;

  const fetchTests = useCallback(async (page = 1, search = '', typeFilter = 'all') => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page: page,
        limit: testsPerPage
      };

      if (search) {
        params.search = search;
      }

      if (sortConfig.key) {
        params.sortBy = sortConfig.key;
        params.sortOrder = sortConfig.direction;
      }

      // Add test type filter
      if (typeFilter !== 'all') {
        params.testType = typeFilter === 'free' ? 'simple' : 'premium';
      }

      const response = await api.get('/tests', { params });

      setTests(response.data.tests);
      setTotalPages(response.data.pagination.totalPages);
      setTotalTests(response.data.pagination.totalTests);
      setCurrentPage(response.data.pagination.currentPage);
    } catch (err) {
      console.error(err);
      setError('Failed to fetch tests');
    } finally {
      setLoading(false);
    }
  }, [sortConfig]);

  useEffect(() => {
    fetchTests(currentPage, searchTerm, testTypeFilter);
  }, [fetchTests, currentPage, searchTerm, testTypeFilter]);

  const openModal = (test = null) => {
    setEditingTest(test);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingTest(null);
    fetchTests(currentPage, searchTerm);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this test?')) {
      try {
        await api.delete(`/admin/tests/${id}`);
        fetchTests(currentPage, searchTerm);
      } catch (err) {
        console.error(err);
        setError('Failed to delete test');
      }
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchTests(1, searchTerm);
  };

  const handleSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
    setCurrentPage(1);
    fetchTests(1, searchTerm);
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleTestTypeFilter = (type) => {
    setTestTypeFilter(type);
    setCurrentPage(1);
    fetchTests(1, searchTerm, type);
  };

  const navigate = useNavigate();

  const handleShowResults = (testId) => {
    navigate(`/tests/${testId}/results`);
  };

  const handleListenOnly = (testId) => {
    navigate(`/speed-selection/${testId}`);
  };
  const handleTest = (testId) => {
    navigate(`/speed-selection/${testId}`);
  };

  const fetchExpectedText = async (testId) => {
    try {
      const { data } = await api.get(`/tests/${testId}/results`);
      setExpectedText(data.expectedText);
    } catch (err) {
      console.error('Failed to fetch expected text:', err);
    }
  };

  const handleViewExpected = (testId) => {
    setSelectedTestId(testId);
    fetchExpectedText(testId);
    setShowExpectedModal(true);
  };

  const closeExpectedModal = () => {
    setShowExpectedModal(false);
    setExpectedText('');
    setSelectedTestId(null);
  };

  useEffect(() => {
    // Inject Kruti Dev font for modal
    const styleId = 'kruti-modal-font-list';
    let style = document.getElementById(styleId);
    if (!style) {
      style = document.createElement('style');
      style.id = styleId;
      style.innerHTML = `
        @font-face {
          font-family: 'KrutiDev010';
          src: url('/fonts/KrutiDev_010.ttf') format('truetype');
          font-weight: normal;
          font-style: normal;
        }
        .kruti-modal-text {
          font-family: 'KrutiDev010', monospace !important;
          font-size: 18px;
          line-height: 1.6;
        }
      `;
      document.head.appendChild(style);
    }

    return () => {
      const existingStyle = document.getElementById(styleId);
      if (existingStyle) {
        document.head.removeChild(existingStyle);
      }
    };
  }, []);
  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pageNumbers = [];
    const delta = 2;

    for (let i = 1; i <= totalPages; i++) {
      if (
        i === 1 ||
        i === totalPages ||
        (i >= currentPage - delta && i <= currentPage + delta)
      ) {
        pageNumbers.push(i);
      } else if (
        i === currentPage - delta - 1 ||
        i === currentPage + delta + 1
      ) {
        pageNumbers.push('...');
      }
    }

    return (
      <div style={paginationStyle}>
        <button
          style={currentPage === 1 || loading ? disabledButtonStyle : buttonStyle}
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1 || loading}
        >
          Previous
        </button>

        {pageNumbers.map((number, index) => (
          number === '...' ? (
            <span key={`ellipsis-${index}`} style={{ padding: '0 0.5rem', color: '#6c757d' }}>...</span>
          ) : (
            <button
              key={number}
              style={number === currentPage ? activeButtonStyle : buttonStyle}
              onClick={() => handlePageChange(number)}
              disabled={loading}
            >
              {number}
            </button>
          )
        ))}

        <button
          style={currentPage === totalPages || loading ? disabledButtonStyle : buttonStyle}
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages || loading}
        >
          Next
        </button>
      </div>
    );
  };

  const getSortIndicator = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === 'ascending' ? ' ▲' : ' ▼';
  };

  const getResultsButtonStyle = (hasAttempts) => {
    if (!hasAttempts) {
      return {
        ...deleteButtonStyle,
        backgroundColor: '#6c757d',
        borderColor: '#6c757d',
        cursor: 'not-allowed',
        opacity: '0.6'
      };
    }
    return resultsButtonStyle;
  };

  if (loading && tests.length === 0) {
    return (
      <div style={containerStyle}>
        <h2 style={{ textAlign: 'center', color: '#2c3e50', marginBottom: '2rem' }}>
          Available Tests
        </h2>
        <div style={gridStyle}>
          {[...Array(6)].map((_, index) => (
            <div key={index} style={skeletonTileStyle}></div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={containerStyle}>
        <div style={{
          textAlign: 'center',
          padding: '3rem',
          background: '#fff5f5',
          border: '1px solid #fed7d7',
          borderRadius: '12px',
          color: '#e53e3e'
        }}>
          <h3 style={{ margin: '0 0 1rem 0' }}>Error Loading Tests</h3>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      {/* Filters Section */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          flexWrap: 'wrap',
          gap: '1rem',
          alignItems: 'flex-end',
          marginBottom: '1.5rem'
        }}
      >
        {/* Test Type Filter Dropdown */}
        <div>
          <select
            value={testTypeFilter}
            onChange={(e) => handleTestTypeFilter(e.target.value)}
            style={{
              width: '150px',
              height: '42px',
              padding: '0.5rem 1rem',
              borderRadius: '6px',
              backgroundColor: '#f8f9fa',
              color: '#6c757d',
              border: '1px solid #e9ecef',
              fontSize: '0.875rem',
              fontWeight: '500',
              cursor: 'pointer',
              boxSizing: 'border-box'
            }}
          >
            <option value="all">All Tests</option>
            <option value="free">FREE</option>
            <option value="premium">PREMIUM</option>
          </select>
        </div>

        {/* Search Form */}
        <form
          onSubmit={handleSearch}
          style={{
            display: 'flex',
            gap: '0.5rem',
            alignItems: 'center'
          }}
        >
          <input
            type="text"
            placeholder="Search tests..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '188px',
              height: '42px',
              padding: '0.5rem 0.75rem',
              borderRadius: '6px',
              border: '1px solid #e9ecef',
              fontSize: '0.875rem',
              boxSizing: 'border-box'
            }}
          />

          <button
            type="submit"
            style={{
              width: '96px',
              height: '42px',
              padding: '0.5rem 1rem',
              borderRadius: '6px',
              backgroundColor: '#3498db',
              color: 'white',
              border: 'none',
              fontSize: '0.875rem',
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            Search
          </button>
        </form>

        {/* Filter Toggle Button */}
        <div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            style={{
              width: '100px',
              height: '42px',
              padding: '0.5rem 1rem',
              borderRadius: '6px',
              backgroundColor: '#f39c12',
              color: 'white',
              border: 'none',
              fontSize: '0.875rem',
              fontWeight: '500',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '4px'
            }}
          >
            {showFilters ? 'Hide' : 'Filter'}
            {showFilters ? '▲' : '▼'}
          </button>
        </div>
      </div>
      {showFilters && (
        <div style={{
          backgroundColor: '#f8f9fa',
          padding: '1.5rem',
          borderRadius: '12px',
          marginBottom: '2rem',
          border: '1px solid #e9ecef'
        }}>

          {/* Sort Controls */}
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '0.5rem',
            justifyContent: 'center'
          }}>
            <button
              onClick={() => handleSort('name')}
              style={{
                padding: '0.5rem 1rem',
                borderRadius: '6px',
                backgroundColor: sortConfig.key === 'name' ? '#3498db' : '#f8f9fa',
                color: sortConfig.key === 'name' ? 'white' : '#6c757d',
                border: `1px solid ${sortConfig.key === 'name' ? '#3498db' : '#e9ecef'}`,
                fontSize: '0.875rem',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              Name{getSortIndicator('name')}
            </button>
            <button
              onClick={() => handleSort('category')}
              style={{
                padding: '0.5rem 1rem',
                borderRadius: '6px',
                backgroundColor: sortConfig.key === 'category' ? '#3498db' : '#f8f9fa',
                color: sortConfig.key === 'category' ? 'white' : '#6c757d',
                border: `1px solid ${sortConfig.key === 'category' ? '#3498db' : '#e9ecef'}`,
                fontSize: '0.875rem',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              Category{getSortIndicator('category')}
            </button>
            <button
              onClick={() => handleSort('timeLimit')}
              style={{
                padding: '0.5rem 1rem',
                borderRadius: '6px',
                backgroundColor: sortConfig.key === 'timeLimit' ? '#3498db' : '#f8f9fa',
                color: sortConfig.key === 'timeLimit' ? 'white' : '#6c757d',
                border: `1px solid ${sortConfig.key === 'timeLimit' ? '#3498db' : '#e9ecef'}`,
                fontSize: '0.875rem',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              Time{getSortIndicator('timeLimit')}
            </button>
            <button
              onClick={() => handleSort('dictationWpm')}
              style={{
                padding: '0.5rem 1rem',
                borderRadius: '6px',
                backgroundColor: sortConfig.key === 'dictationWpm' ? '#3498db' : '#f8f9fa',
                color: sortConfig.key === 'dictationWpm' ? 'white' : '#6c757d',
                border: `1px solid ${sortConfig.key === 'dictationWpm' ? '#3498db' : '#e9ecef'}`,
                fontSize: '0.875rem',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              Speed{getSortIndicator('dictationWpm')}
            </button>
            <button
              onClick={() => handleSort('wordCount')}
              style={{
                padding: '0.5rem 1rem',
                borderRadius: '6px',
                backgroundColor: sortConfig.key === 'wordCount' ? '#3498db' : '#f8f9fa',
                color: sortConfig.key === 'wordCount' ? 'white' : '#6c757d',
                border: `1px solid ${sortConfig.key === 'wordCount' ? '#3498db' : '#e9ecef'}`,
                fontSize: '0.875rem',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              Word Count{getSortIndicator('wordCount')}
            </button>
            <button
              onClick={() => handleSort('audioDuration')}
              style={{
                padding: '0.5rem 1rem',
                borderRadius: '6px',
                backgroundColor: sortConfig.key === 'audioDuration' ? '#3498db' : '#f8f9fa',
                color: sortConfig.key === 'audioDuration' ? 'white' : '#6c757d',
                border: `1px solid ${sortConfig.key === 'audioDuration' ? '#3498db' : '#e9ecef'}`,
                fontSize: '0.875rem',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              Audio Time{getSortIndicator('audioDuration')}
            </button>
            <button
              onClick={() => handleSort('attemptCount')}
              style={{
                padding: '0.5rem 1rem',
                borderRadius: '6px',
                backgroundColor: sortConfig.key === 'attemptCount' ? '#3498db' : '#f8f9fa',
                color: sortConfig.key === 'attemptCount' ? 'white' : '#6c757d',
                border: `1px solid ${sortConfig.key === 'attemptCount' ? '#3498db' : '#e9ecef'}`,
                fontSize: '0.875rem',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              Most Attempted{getSortIndicator('attemptCount')}
            </button>
          </div>
        </div>
      )}

      {loading && (
        <div style={loadingStyle}>
          <div style={spinnerStyle}></div>
          <p style={{ color: '#6c757d', marginTop: '1rem' }}>Loading test results...</p>
        </div>
      )}

      {tests.length === 0 && !loading ? (
        <div style={{
          textAlign: 'center',
          padding: '4rem 2rem',
          background: '#f8f9fa',
          borderRadius: '12px',
          border: '1px solid #e9ecef'
        }}>
          <h3 style={{ color: '#6c757d', margin: '0 0 1rem 0' }}>No test results available</h3>
          <p style={{ color: '#6c757d' }}>Complete some tests to see your results here.</p>
        </div>
      ) : (
        <>
          <div>
            <div style={gridStyle}>
              {tests.map((test) => (
                <div
                  key={test.id}
                  style={baseTileStyle}
                  className="tile-hover"
                >
                  <div style={{ ...tileHeaderStyle, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1 }}>
                      <Link
                        to={`/tests/${test.id}`}
                        style={tileTitleStyle}
                      >
                        {test.name}
                      </Link>
                      {test.testType === 'simple' && <span style={badgeStyle}>FREE</span>}
                      {test.testType === 'premium' && <span style={premiumBadgeStyle}>PREMIUM</span>}
                      {test.testType === 'go-live' && <span style={liveBadgeStyle}>LIVE</span>}
                      {test.testType === 'premium' && (!user || !user.isPremium) && <span style={lockIconStyle}>🔒</span>}
                      <span style={tileCategoryStyle}>{test.category.toUpperCase()}</span>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (test.userRank !== null) {
                          handleViewExpected(test.id);
                        }
                      }}
                      disabled={test.userRank === null}
                      style={{
                        background: test.userRank !== null ? 'linear-gradient(135deg, #10b981, #059669)' : '#6c757d',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '0.4rem 0.8rem',
                        fontSize: '0.8rem',
                        cursor: test.userRank === null ? 'not-allowed' : 'pointer',
                        opacity: test.userRank === null ? 0.6 : 1,
                        fontWeight: '500',
                        transition: 'all 0.2s ease',
                        minWidth: '80px',
                        height: 'auto'
                      }}
                    >
                      👁️ View PDF
                    </button>
                  </div>

                  <div style={tileStatsStyle}>
                    {/* Info Tiles */}
                    <div style={statItemStyle}><p style={statLabelStyle}>Time</p><p style={statValueStyle}>{formatTime(test.timeLimit)}</p></div>
                    <div style={statItemStyle}><p style={statLabelStyle}>Speed</p><p style={statValueStyle}>{test.dictationWpm} WPM</p></div>
                    <div style={statItemStyle}><p style={statLabelStyle}>Words</p><p style={statValueStyle}>{test.wordCount}</p></div>
                    <div style={statItemStyle}><p style={statLabelStyle}>Audio Time</p><p style={statValueStyle}>{formatTime(test.audioDuration)}</p></div>
                    <div style={statItemStyle}><p style={statLabelStyle}>Rank</p><p style={statValueStyle}>{test.userRank ? `#${test.userRank}` : 'NA'}</p></div>
                    <div style={statItemStyle}><p style={statLabelStyle}>Attempts</p><p style={statValueStyle}>{test.attemptCount || 0}</p></div>

                    {/* Buttons Section */}
                    <div style={statItemStyle}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleShowResults(test.id);
                        }}
                        disabled={test.attemptCount === 0}
                        title={test.attemptCount === 0 ? "No attempts yet" : "View results"}
                        className="results-btn"
                        style={getResultsButtonStyle(test.attemptCount > 0)}
                      >
                        📊 {test.attemptCount > 0 ? 'Results' : 'No Data'}
                      </button>
                    </div>

                    <div style={statItemStyle}>
                      {test.testType === 'premium' && (!user || !user.isPremium) ? (
                        <button className="upgrade-btn" style={upgradeButtonStyle}>
                          ⚡ Upgrade
                        </button>
                      ) : (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleListenOnly(test.id);
                          }}
                          className="dictation-btn"
                          style={dictationButtonStyle}
                        >
                          🎧 Dictation
                        </button>
                      )}
                    </div>

                    <div style={statItemStyle}>
                      {test.testType === 'premium' && (!user || !user.isPremium) ? (
                        <button className="upgrade-btn" style={upgradeButtonStyle}>
                          ⚡ Upgrade
                        </button>
                      ) : (
                        <Link to={`/speed-selection/${test.id}?source=play`}>
                          <button className="play-btn" style={playButtonStyle}>
                            ▶ Play
                          </button>
                        </Link>
                      )}
                    </div>
                  </div>

                  {/* Admin Controls */}
                  {user && user.isAdmin && (
                    <div style={{ textAlign: 'center', marginTop: '1rem' }}>
                      <div style={{ display: 'inline-flex', gap: '0.5rem', justifyContent: 'center' }}>
                        <button
                          onClick={() => handleDelete(test.id)}
                          className="delete-btn"
                          style={deleteButtonStyle}
                        >
                          🗑️ Delete
                        </button>
                        <button
                          onClick={() => openModal(test)}
                          className="edit-btn"
                          style={editButtonStyle}
                        >
                          ✏️ Edit
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {renderPagination()}
          </div>
        </>


      )
      }

      {showModal && <UploadModal onClose={closeModal} test={editingTest} />}

      {showExpectedModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={closeExpectedModal}
        >
          <div
            style={{
              backgroundColor: 'white',
              padding: '2rem',
              borderRadius: '16px',
              maxWidth: '80%',
              maxHeight: '80%',
              overflow: 'auto',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              position: 'relative',
              minWidth: '400px',
              userSelect: 'none',
              WebkitUserSelect: 'none',
              MozUserSelect: 'none',
              msUserSelect: 'none'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginTop: 0, color: '#2c3e50', textAlign: 'center', marginBottom: '1rem' }}>
              PDF View
            </h3>
            <div
              className="kruti-modal-text"
              style={{
                border: '1px solid #e9ecef',
                borderRadius: '8px',
                padding: '1rem',
                minHeight: '200px',
                maxHeight: '400px',
                whiteSpace: 'pre-wrap',
                backgroundColor: '#f8f9fa',
                marginBottom: '1rem',
                overflow: 'auto',
                userSelect: 'none',
                WebkitUserSelect: 'none',
                MozUserSelect: 'none',
                msUserSelect: 'none'
              }}
              dangerouslySetInnerHTML={{ __html: expectedText }}
            />
            <div style={{ textAlign: 'center' }}>
              <button
                onClick={closeExpectedModal}
                style={{
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  transition: 'all 0.3s ease',
                  fontSize: '1rem'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#5a6268';
                  e.target.style.transform = 'scale(1.02)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#6c757d';
                  e.target.style.transform = 'scale(1)';
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div >
  );
};

export default TestList;
